<template>
  <v-app
    :class="{
      'app--impersonating': authStore.isImpersonating,
      'app--switched-org': authStore.isInSwitchedOrg,
      'app--dark-page': uiStore.isDarkPage,
    }"
    :style="{ background: uiStore.isDarkPage ? '#000' : 'var(--background-gradient)' }"
  >
    <!-- Ganzseitiges dunkles Design (Look der Willkommensseite): Aurora-Glow
         hinter dem gesamten Seiteninhalt statt nur hinter einem Panel. -->
    <div v-if="uiStore.isDarkPage" aria-hidden="true" class="app__aurora" />

    <ImpersonationBanner />
    <AppUpdateBanner />
    <ActiveOrgBanner />

    <Navigation v-if="$route.meta.isMainView && !isHubFocus" />

    <v-main class="layout-main">
      <!-- Fokus-Modus: Eine Einstellungsseite wurde aus der Willkommensseite
           heraus geöffnet. Statt der Fachanwendungs-Navigation erscheint nur
           ein Zurück-Link, damit der Nutzer wieder im Hub landet. -->
      <div v-if="isHubFocus" class="hub-focus-bar">
        <!-- Kundenfeedback 05.09.26 (III): In allen Einstellungsseiten führt
             links oben das KARS-Connection-Logo zur Willkommensseite — wie in
             KARS-Schaden das App-Logo zur Startseite. -->
        <RouterLink
          :to="{ name: 'PageWelcomeHub' }"
          class="hub-focus-brand"
          :title="$gt('Zur Willkommensseite')"
          :aria-label="$gt('Zur Willkommensseite')"
        >
          <img :src="connectionLogo" alt="" class="hub-focus-brand__logo" />
        </RouterLink>
        <span aria-hidden="true" class="hub-focus-sep" />
        <button type="button" class="hub-focus-back" @click="backFromHubFocus">
          <ArrowLeft :size="18" />
          <span>{{ $gt('Zurück') }}</span>
        </button>
        <RouterLink :to="{ name: 'PageWelcomeHub' }" class="hub-focus-home">
          <Home :size="16" />
          <span>{{ $gt('Willkommensseite') }}</span>
        </RouterLink>
      </div>

      <!-- Kundenfeedback 04.10.26, I: Die Fußzeile (Datenschutz, Impressum,
           Website, Version) braucht man selten. Der Inhalt füllt deshalb den
           ganzen sichtbaren Bereich; die Fußzeile liegt direkt darunter und
           ist per Scrollen erreichbar – auf jeder Seite. -->
      <div class="content-wrapper" :class="{ 'content-wrapper--fills-viewport': showFooter }">
        <router-view />
      </div>

      <Footer v-if="showFooter" />
    </v-main>

    <Detailview />
    <DialogSupport v-model:open="uiStore.isSupportDialogOpen" />
    <Snackbar />
    <LicenseQuotaDialog
      :visible="licenseQuotaStore.visible"
      :quota="licenseQuotaStore.quota"
      @close="licenseQuotaStore.close"
    />
    <PaymentRequiredGate v-if="authStore.isAuthenticated" />
    <CookieWarning />
    <!-- Der Bug-Report-Dialog bleibt; sein schwebender Knopf ist im KARS-KI
         Assistenten aufgegangen („Fehler melden“ im Panel). -->
    <BugReportButton v-if="authStore.isAuthenticated" hide-trigger />
    <!-- KARS-KI Support-Assistent (Spec 2026-11-04): einziger schwebender
         Knopf unten rechts (Hilfe und Fehler melden) und das Chat-Panel. -->
    <AssistantLauncher v-if="authStore.isAuthenticated" />
    <AssistantPanel v-if="authStore.isAuthenticated" />
    <ActiveCallBar />
    <CallConfirmDialog />
    <CallNotePrompt />
    <MinimizedWindowDock />
    <!-- App-globales, schwebendes Unterhaltungs-Fenster (Kundenfeedback
         30.08.26): lebt neben der Dock-Leiste auf App-Ebene, damit Fenster
         und minimierter Chip jede Navigation innerhalb des Falls überleben. -->
    <ConversationWindow
      :model-value="conversationWindowStore.isOpen"
      :conversation="conversationWindowStore.conversation"
      @update:model-value="onConversationWindowToggle"
      @sent="conversationWindowStore.notifySent()"
    />
    <!-- App-globales Verfassen-Fenster „Neue Nachricht" (Kundenfeedback
         16.10.2026, Kanzlei Polz): Mail schreiben und währenddessen z. B. ins
         Cockpit wechseln. Nicht-modal, verschieb-/minimierbar; die Ansichten
         öffnen es über den composeWindowStore. -->
    <NewMessageDialog
      v-if="authStore.isAuthenticated"
      ref="composeDialog"
      @closed="composeWindowStore.notifyClosed()"
      @sent="composeWindowStore.notifySent($event)"
    />
    <!-- Eskalation dringender Nachrichten: globales rotes Popup, das auf
         jeder Seite erscheint, solange dringende ungelesene Nachrichten
         existieren (gespeist von NotificationBell bzw. eigenem Fallback-Poll). -->
    <UrgentMessageAlert v-if="authStore.isAuthenticated" />
  </v-app>
</template>

<script>
import Navigation from "@/components/app/Navigation.vue";
import Footer from "@/components/app/Footer.vue";
import Detailview from "@/components/app/detailview/Detailview.vue";
import DialogSupport from "@/components/app/DialogSupport.vue";
import Snackbar from "@/components/app/Snackbar.vue";
import LicenseQuotaDialog from "@/components/billing/LicenseQuotaDialog.vue";
import PaymentRequiredGate from "@/components/billing/PaymentRequiredGate.vue";
import CookieWarning from "@/components/app/DialogCookieWarning.vue";
import BugReportButton from "@/components/app/BugReportButton.vue";
import AssistantLauncher from "@/components/assistant/AssistantLauncher.vue";
import AssistantPanel from "@/components/assistant/AssistantPanel.vue";
import ImpersonationBanner from "@/components/app/ImpersonationBanner.vue";
import AppUpdateBanner from "@/components/app/AppUpdateBanner.vue";
import ActiveOrgBanner from "@/components/app/ActiveOrgBanner.vue";
import ActiveCallBar from "@/components/app/ActiveCallBar.vue";
import CallConfirmDialog from "@/components/app/CallConfirmDialog.vue";
import CallNotePrompt from "@/components/app/CallNotePrompt.vue";
import MinimizedWindowDock from "@/components/windows/MinimizedWindowDock.vue";
import UrgentMessageAlert from "@/components/app/UrgentMessageAlert.vue";
import ConversationWindow from "@/components/messages/ConversationWindow.vue";
import NewMessageDialog from "@/pages/pageMessages/NewMessageDialog.vue";

import { ArrowLeft, Home } from "lucide-vue-next";
import connectionLogo from "@/assets/images/logo_black_shadow.png";

import { useUiStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { useLicenseQuotaStore } from "@/stores/licenseQuotaStore";
import { useConversationWindowStore } from "@/stores/conversationWindowStore";
import { useComposeWindowStore } from "@/stores/composeWindowStore";
import { subscribeAuthChanges } from "@/utils/authSync";

// Fokus-Revalidierung der Session höchstens alle 15 Sekunden — häufige
// Fensterwechsel sollen keine Request-Flut auslösen.
const SESSION_REVALIDATE_MIN_INTERVAL_MS = 15000;

export default {
  name: "App",

  components: {
    Navigation,
    Footer,
    Detailview,
    DialogSupport,
    Snackbar,
    LicenseQuotaDialog,
    PaymentRequiredGate,
    CookieWarning,
    BugReportButton,
    AssistantLauncher,
    AssistantPanel,
    ActiveCallBar,
    CallConfirmDialog,
    CallNotePrompt,
    ImpersonationBanner,
    AppUpdateBanner,
    ActiveOrgBanner,
    MinimizedWindowDock,
    UrgentMessageAlert,
    ConversationWindow,
    NewMessageDialog,
    ArrowLeft,
    Home,
  },

  data() {
    return { connectionLogo };
  },

  computed: {
    // Aus der Willkommensseite geöffnete Einstellungsseiten laufen im
    // Fokus-Modus: keine Fachanwendungs-Navigation, nur ein Zurück-Link.
    isHubFocus() {
      // Plattform-Seiten (connectionPage, z. B. Organisations- und
      // Benutzerverwaltung, Rechnungen) laufen immer im Fokus-Modus: Sie
      // gehören zu KARS-Connection, nicht zu KARS-Schaden (Feedback 05.09.26, IV).
      return (
        this.$route.meta.isMainView === true &&
        (this.$route.query.from === "hub" || this.$route.meta.connectionPage === true)
      );
    },
    showFooter() {
      return this.$route.meta.isMainView === true && !this.isHubFocus;
    },
    isLoggedIn() {
      return useAuthStore().isAuthenticated;
    },
    /** Reaktiver Zähler des Verfassen-Fensters (Store direkt, nicht über `this`). */
    composeOpenTick() {
      return useComposeWindowStore().openTick;
    },
  },

  watch: {
    /** Beim Logout die globalen Fenster schließen. */
    isLoggedIn(value) {
      if (!value) {
        useConversationWindowStore().close();
        if (useComposeWindowStore().isOpen) this.$refs.composeDialog?.close();
      }
    },
    /** Öffnen-Anforderung einer Ansicht an das globale Verfassen-Fenster. */
    composeOpenTick(tick) {
      if (!tick) return;
      this.$nextTick(() => {
        this.$refs.composeDialog?.open({ ...useComposeWindowStore().options });
      });
    },
  },

  created() {
    this.uiStore = useUiStore();
    this.authStore = useAuthStore();
    this.licenseQuotaStore = useLicenseQuotaStore();
    this.conversationWindowStore = useConversationWindowStore();
    this.composeWindowStore = useComposeWindowStore();
  },

  mounted() {
    // Anmeldeänderungen anderer Tabs/Fenster (Logout, Login mit anderem
    // Unternehmen) sofort übernehmen — alle Fenster teilen sich den
    // Session-Cookie, ihr In-Memory-Zustand darf nicht auseinanderlaufen.
    this.unsubscribeAuthSync = subscribeAuthChanges((event) => {
      this.authStore.handleExternalAuthChange(event);
    });

    // Sicherheitsnetz: Beim Fokus-/Sichtbarkeitswechsel die Session gegen
    // den Server abgleichen (gedrosselt). Fängt Abweichungen ab, die kein
    // Sync-Event erreicht hat.
    this.lastSessionRevalidateAt = 0;
    this.onWindowFocus = () => this.revalidateSessionOnFocus();
    window.addEventListener("focus", this.onWindowFocus);
    document.addEventListener("visibilitychange", this.onWindowFocus);
  },

  beforeUnmount() {
    if (this.unsubscribeAuthSync) this.unsubscribeAuthSync();
    window.removeEventListener("focus", this.onWindowFocus);
    document.removeEventListener("visibilitychange", this.onWindowFocus);
  },

  methods: {
    /** Schließen des Unterhaltungs-Fensters (X-Button) → Store aufräumen. */
    onConversationWindowToggle(open) {
      if (!open) this.conversationWindowStore.close();
    },

    revalidateSessionOnFocus() {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - this.lastSessionRevalidateAt < SESSION_REVALIDATE_MIN_INTERVAL_MS) return;
      this.lastSessionRevalidateAt = now;
      void this.authStore.revalidateSession();
    },

    // Kundenfeedback 19.08.26 (III): Der Zurück-Pfeil führt auf die zuvor
    // besuchte Seite zurück (z. B. von den Benutzereinstellungen in die
    // Organisationseinstellungen) — nicht pauschal auf die Willkommensseite.
    // Ohne App-interne Historie (Direktaufruf) bleibt der Hub das Ziel.
    backFromHubFocus() {
      // Kundenreview 23.08.26 (IV): Wenn der vorherige History-Eintrag die
      // Login-/Registrierungsseite ist (z. B. nach Erstanmeldung oder einem
      // Guard-Redirect), darf "Zurück" dort nicht landen — dann immer zur
      // Willkommensseite.
      const backPath =
        typeof window.history.state?.back === "string" ? window.history.state.back : "";
      const isAuthPage =
        backPath.startsWith("/einloggen") ||
        backPath.startsWith("/signup") ||
        backPath.startsWith("/registrierung");
      if (backPath && !isAuthPage) {
        this.$router.back();
        return;
      }
      this.$router.push({ name: "PageWelcomeHub" }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.layout-main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Fokus-Modus-Leiste: schlanker Zurück-Link zur Willkommensseite. */
.hub-focus-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: saturate(180%) blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.hub-focus-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--dark-color, #1d1d1f);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.18s ease,
    transform 0.18s ease;
}

.hub-focus-back:hover {
  background: rgba(0, 113, 227, 0.1);
}

/* KARS-Connection-Logo links oben: Link zur Willkommensseite. */
.hub-focus-brand {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px 4px 0;
  border-radius: 10px;
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.hub-focus-brand:hover {
  opacity: 0.85;
  transform: translateY(-1px);
}

.hub-focus-brand:focus-visible {
  outline: 2px solid var(--primary-color, #0071e3);
  outline-offset: 2px;
}

.hub-focus-brand__logo {
  display: block;
  height: 38px;
  width: auto;
}

.hub-focus-sep {
  width: 1px;
  height: 26px;
  margin: 0 8px;
  background: rgba(0, 0, 0, 0.12);
}

.hub-focus-home {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  color: var(--grey-color, #5f5f5f);
  font-size: 0.88rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.18s ease, color 0.18s ease;
}

.hub-focus-home:hover {
  background: rgba(0, 113, 227, 0.1);
  color: var(--dark-color, #1d1d1f);
}

.hub-focus-home:focus-visible {
  outline: 2px solid var(--primary-color, #0071e3);
  outline-offset: 2px;
}

@media (max-width: 600px) {
  .hub-focus-home span {
    display: none;
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   Ganzseitiges dunkles Design im Look der Willkommensseite. Wird von Seiten
   ueber uiStore.isDarkPage aktiviert; der Aurora-Glow liegt hinter dem
   gesamten Seiteninhalt, nicht nur hinter einem Panel.
   ───────────────────────────────────────────────────────────────────────── */
.app--dark-page {
  color-scheme: dark;
}

.app__aurora {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(120% 80% at 50% 0%, rgba(10, 132, 255, 0.12), transparent 55%),
    radial-gradient(90% 70% at 88% 105%, rgba(0, 64, 221, 0.16), transparent 60%),
    linear-gradient(180deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.5));
}

.app--dark-page .layout-main {
  position: relative;
  z-index: 1;
}

.app--dark-page .hub-focus-bar {
  background: rgba(0, 0, 0, 0.55);
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.app--dark-page .hub-focus-back {
  color: #f5f5f7;
}

.app--dark-page .hub-focus-back:hover {
  background: rgba(10, 132, 255, 0.16);
}

.app--dark-page .hub-focus-sep {
  background: rgba(255, 255, 255, 0.18);
}

.app--dark-page .hub-focus-home {
  color: rgba(245, 245, 247, 0.8);
}

.app--dark-page .hub-focus-brand__logo {
  filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.6));
}

.hub-focus-back:focus-visible {
  outline: 2px solid var(--primary-color, #0071e3);
  outline-offset: 2px;
}

.content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Inhalt bis zur Unterkante des Fensters, Fußzeile erst darunter. */
.content-wrapper--fills-viewport {
  flex: 1 0 auto;
  min-height: calc(100vh - var(--v-layout-top, 0px) - var(--app-top-banner-offset, 0px));
  min-height: calc(100dvh - var(--v-layout-top, 0px) - var(--app-top-banner-offset, 0px));
}

/* Fest stehende Hinweisleisten oben: SuperAdmin-Sitzung (ImpersonationBanner)
   und „Organisation gewechselt" (ActiveOrgBanner), je 44px hoch. Sie schieben
   Vuetifys fest stehende App-Leisten und den Inhalt um ihre Gesamthöhe nach
   unten; sind beide sichtbar, stehen sie übereinander.
   --app-top-banner-offset ist die Gesamthöhe. Für Inhalte mit eigenen fest
   stehenden Leisten (Fragebogen-Hülle): --v-layout-top kennt sie nicht.
   --app-impersonation-offset bleibt als gleichwertiger Alias bestehen. */
.app--impersonating,
.app--switched-org {
  --app-top-banner-offset: 44px;
  --app-impersonation-offset: var(--app-top-banner-offset);
}

.app--impersonating.app--switched-org {
  --app-top-banner-offset: 88px;
  /* ActiveOrgBanner steht unter dem Banner der SuperAdmin-Sitzung. */
  --app-org-banner-top: 44px;
}

.app--impersonating :deep(.v-app-bar),
.app--switched-org :deep(.v-app-bar) {
  top: var(--app-top-banner-offset) !important;
}

.app--impersonating :deep(.v-main),
.app--switched-org :deep(.v-main) {
  padding-top: calc(var(--v-layout-top, 0px) + var(--app-top-banner-offset)) !important;
}
</style>
